import { Intercom } from '@intercom/messenger-js-sdk';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import type React from 'react';
import { useLayoutEffect } from 'react';
import { INTERCOM_APP_ID } from '../../lib/constants.ts';
import type { RouterOutputs } from '../../utils/api.ts';
import { getFullName } from '../../utils/name.ts';

type Props = {
    user: NonNullable<RouterOutputs['platoUser']['me']['getMe']>;
    children: React.ReactNode | React.ReactNode[];
};

export function IntercomProvider({ user, children }: Props) {
    // re-renders on languageChanged, so a pick in the language picker re-runs the effect
    const locale = useResolvedLocale();

    // the Intercom dep ultimately mutates the DOM so we use useLayoutEffect
    useLayoutEffect(() => {
        if (!INTERCOM_APP_ID) {
            return;
        }
        try {
            // the library automatically handles loading/updating existing instances
            Intercom({
                api_base: 'https://api-iam.intercom.io',
                app_id: INTERCOM_APP_ID,
                name: getFullName(user.firstName, user.lastName),
                email: user.email,
                customer_id: user.platoCustomer.id,
                customer_name: user.platoCustomer.name,
                created_at: user.createdAt.getTime() / 1000,
                hide_default_launcher: true,
                language_override: locale,
            });
        } catch (error) {
            // don't crash the page if we can't load Intercom
            console.error(`Error loading Intercom`, error);
        }
    }, [user, locale]);

    return children;
}
